/**
 * Impression plugin: emits section/component impressions when visible.
 */
import type { PluginContext } from "../lib/types";

import { registerPlugin } from "./register";

registerPlugin("impressions", (ctx: PluginContext) => {
  const selector =
    "[data-prospix-impression], [data-track-impression], [data-prospix-section]";
  const seen = new Set<string>();
  const firstSeenAt = new WeakMap<Element, number>();
  let observer: IntersectionObserver | undefined;
  let scanTimer: ReturnType<typeof setInterval> | undefined;

  const getKey = (el: Element) => {
    const id = (el as HTMLElement).id || "";
    const cls = (el as HTMLElement).className || "";
    const label =
      (el as HTMLElement).getAttribute("data-prospix-impression") ||
      (el as HTMLElement).getAttribute("data-track-impression") ||
      (el as HTMLElement).getAttribute("data-prospix-section") ||
      "";
    return `${el.tagName.toLowerCase()}|${id}|${cls}|${label}`;
  };

  const emitImpression = (el: Element) => {
    if (!ctx.canTrackCategory("impression_analytics")) return;
    const key = getKey(el);
    if (seen.has(key)) return;
    seen.add(key);

    const htmlEl = el as HTMLElement;
    const id = htmlEl.id || "";
    const cls = htmlEl.className || "";
    const label =
      htmlEl.getAttribute("data-prospix-impression") ||
      htmlEl.getAttribute("data-track-impression") ||
      htmlEl.getAttribute("data-prospix-section") ||
      undefined;

    ctx.track("element_impression", {
      tag: el.tagName.toLowerCase(),
      id_hash: id ? ctx.redact.hashString(id) : undefined,
      class_hash: cls ? ctx.redact.hashString(cls) : undefined,
      label,
      visible_ms: 500,
    });
  };

  const observeAll = () => {
    if (!observer) return;
    const nodes = document.querySelectorAll(selector);
    nodes.forEach((node) => observer?.observe(node));
  };

  return {
    onInit() {
      if (!ctx.canTrackCategory("impression_analytics")) return;
      if (typeof IntersectionObserver === "undefined") return;

      observer = new IntersectionObserver(
        (entries) => {
          const now = Date.now();
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
              const started = firstSeenAt.get(entry.target) ?? now;
              firstSeenAt.set(entry.target, started);
              if (now - started >= 500) {
                emitImpression(entry.target);
                observer?.unobserve(entry.target);
              }
              return;
            }
            firstSeenAt.delete(entry.target);
          });
        },
        { threshold: [0.5] },
      );

      observeAll();
      scanTimer = setInterval(observeAll, 3000);
    },
    onUnload() {
      if (scanTimer) clearInterval(scanTimer);
      observer?.disconnect();
      seen.clear();
    },
  };
});
